'use client';

import { type FreeGiftsData } from '../../../../types/front.graphql';
import { cartItemReferenceStoreAPI } from '@kidslabs/module-quote';
import { useObservable } from '@legendapp/state/react';
import { batch, whenReady, type Observable } from '@legendapp/state';
import { type DisabledGiftItemObservable } from '../../../../utils/helpers';
import React, { useEffect } from 'react';
import { type SelectedGift } from '../../types/gift-selection.types';
import { GiftData as LocalGiftData } from '../gifts-partials/gift-data/gift-data';
import type { GiftLayoutEnum } from '../../../../enums';

interface GiftDataProps {
    giftData: FreeGiftsData;
    // Title to display - optional, will use rule name as fallback
    title?: string;
    // Handler for selecting a gift
    onSelectGift: (gift: SelectedGift) => void;
    // Currently selected gifts observable - can be undefined
    selectedGifts$?: Observable<SelectedGift[]>;
    // Observable product quantity - used in cart context instead of cartItemReference stepper
    productQuantity$?: Observable<number>;
    // Observable selection round
    selectionRound$?: Observable<number>;
    sessionId: string;
    layout?: GiftLayoutEnum;
    /** When true, rules are never disabled and selecting an ineligible rule triggers auto-fill */
    enableAutoFill?: boolean;
    /** Called when user selects a gift that requires more quantity */
    onAutoFillRequired?: (requiredQuantity: number, gift: SelectedGift) => void;
}

export const GiftData = ({
    giftData,
    title,
    onSelectGift,
    selectedGifts$,
    productQuantity$,
    selectionRound$,
    sessionId,
    layout,
    enableAutoFill,
    onAutoFillRequired,
}: GiftDataProps) => {
    const quantityStepper$ = cartItemReferenceStoreAPI.state.getQuantityStepperObservable();
    const itemDisabled$: DisabledGiftItemObservable = useObservable<boolean>(false);

    // Use provided title or fallback to rule name
    const displayTitle = title ?? giftData?.rule?.name ?? 'N/A';

    // Determine if we're in cart context (productQuantity$ is provided)
    const isCartContext = !!productQuantity$;

    const processCounterValue = (counterValue: number) => {
        batch(() => {
            const isQuantityMatched = (giftData.rule?.discount_step ?? 0) <= counterValue;

            if (enableAutoFill) {
                // Auto-fill mode: track eligibility for visual hint but do NOT block interaction
                // and do NOT remove selected gifts — auto-fill will handle quantity adjustment
                itemDisabled$.set(!isQuantityMatched);
            } else {
                // Legacy behavior: disable and remove gifts when quantity doesn't match
                itemDisabled$.set(!isQuantityMatched);

                if (!isQuantityMatched) {
                    // Remove gifts from this rule when quantity doesn't match
                    const ruleId = giftData?.rule?.rule_id;
                    if (ruleId && selectedGifts$) {
                        const currentGifts = selectedGifts$.get() ?? [];
                        const giftsToRemove = currentGifts?.filter(gift => gift.ruleId === ruleId);
                        if (giftsToRemove.length > 0) {
                            const remainingGifts = currentGifts.filter(gift => gift.ruleId !== ruleId);
                            selectedGifts$.set(remainingGifts);
                        }
                    }
                }
            }
        });
    };

    // Cart context: Listen to productQuantity$ changes
    useEffect(() => {
        if (!isCartContext || !productQuantity$) return;

        // Process initial value
        const initialValue = productQuantity$.peek();
        if (initialValue) {
            processCounterValue(initialValue);
        }

        // Listen to changes
        const unsubscribe = productQuantity$.onChange(({ value }) => {
            processCounterValue(value);
        });

        return () => {
            unsubscribe?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCartContext, productQuantity$]);

    // PDP context: Listen to quantityStepper changes
    // ✅ IMPORTANT: Match mobile pattern - call directly without useEffect for immediate execution
    if (!isCartContext && quantityStepper$) {
        // Process with whenReady for initial value
        whenReady(quantityStepper$, value => {
            processCounterValue(value);
        });

        // Subscribe to changes
        quantityStepper$.onChange(({ value }: { value: number }) => {
            processCounterValue(value);
        });
    }

    // ✅ FIX: Handle undefined selectedGifts$ case
    if (!selectedGifts$) {
        console.warn('GiftData: selectedGifts$ is undefined, component may not function properly');
        return (
            <LocalGiftData
                layout={layout}
                giftData={giftData}
                title={displayTitle}
                itemDisabled$={itemDisabled$}
                onSelectGift={onSelectGift}
                // Pass observables instead of primitives
                productQuantity$={productQuantity$}
                selectionRound$={selectionRound$}
                sessionId={sessionId}
                enableAutoFill={enableAutoFill}
                onAutoFillRequired={onAutoFillRequired}
            />
        );
    }

    return (
        <LocalGiftData
            layout={layout}
            giftData={giftData}
            title={displayTitle}
            itemDisabled$={itemDisabled$}
            onSelectGift={onSelectGift}
            selectedGifts$={selectedGifts$}
            // Pass observables instead of primitives
            productQuantity$={productQuantity$}
            selectionRound$={selectionRound$}
            sessionId={sessionId}
            enableAutoFill={enableAutoFill}
            onAutoFillRequired={onAutoFillRequired}
        />
    );
};
